/* ================================================================
   Same-store link normaliser (runs before everything else)
   Shopify menus can contain the store's own full domain, e.g.
   "https://www.stitchingcotton.com/". Inside the Theme Editor preview (which
   runs on the myshopify domain) such a link leaves the preview and Shopify
   shows "Failed to load the page / unsupported URL". Every header link that
   points to THIS store is rewritten to a path on the current domain, so it
   works the same in the Theme Editor, on the preview link and on the live site.
   ================================================================ */
(() => {
  const storeHosts = () => {
    const hosts = new Set([window.location.hostname.toLowerCase()]);
    try {
      const canonical = document.querySelector('link[rel="canonical"]')?.href;
      if (canonical) hosts.add(new URL(canonical).hostname.toLowerCase());
    } catch (_) {}
    try {
      if (window.Shopify?.shop) hosts.add(String(window.Shopify.shop).toLowerCase());
    } catch (_) {}
    [...hosts].forEach((h) => hosts.add(h.startsWith('www.') ? h.slice(4) : `www.${h}`));
    return hosts;
  };

  const toLocal = (value, hosts) => {
    if (!value) return null;
    try {
      const u = new URL(value, window.location.href);
      if (!/^https?:$/i.test(u.protocol)) return null;
      if (u.origin === window.location.origin) return null;
      if (!hosts.has(u.hostname.toLowerCase())) return null;
      return `${u.pathname || '/'}${u.search}${u.hash}`;
    } catch (_) {
      return null;
    }
  };

  const normalise = (root) => {
    if (!root || root.dataset.refLinksNormalised === '1') return;
    root.dataset.refLinksNormalised = '1';
    const hosts = storeHosts();
    root.querySelectorAll('a[href]').forEach((a) => {
      const local = toLocal(a.getAttribute('href'), hosts);
      if (local) a.setAttribute('href', local);
    });
    root.querySelectorAll('[data-menu-url]').forEach((el) => {
      const local = toLocal(el.getAttribute('data-menu-url'), hosts);
      if (local) el.setAttribute('data-menu-url', local);
    });
  };

  document.querySelectorAll('.ref-header').forEach(normalise);
  document.addEventListener('shopify:section:load', (e) => {
    const target = e.target;
    const root = target?.matches?.('.ref-header') ? target : target?.querySelector?.('.ref-header');
    normalise(root);
  });
})();

/* Reference Mega Header — core (search, mega menu, cart drawer, department context) */
(() => {
  const init = (root) => {
    if (!root || root.dataset.refInit === '1') return;
    root.dataset.refInit = '1';

    const $ = (sel, ctx = root) => ctx.querySelector(sel);
    const $$ = (sel, ctx = root) => [...ctx.querySelectorAll(sel)];
    const searchLayer = $('.ref-search-layer');
    const searchInput = $('.ref-search-input');
    const searchForm = $('.ref-search-form');
    const searchResults = $('.ref-search-results');
    const searchStatus = $('.ref-search-status');
    const mega = $('.ref-mega-layer');
    const cartDrawer = $('.ref-cart-drawer');
    const cartShade = $('.ref-cart-shade');
    const cartDynamic = $('.ref-cart-dynamic');
    const rootUrl = root.dataset.rootUrl || '/';
    const cartUrl = root.dataset.cartUrl || '/cart';
    const checkoutUrl = root.dataset.checkoutUrl || '/checkout';
    const accountUrl = root.dataset.accountUrl || root.dataset.accountLoginUrl || '/';
    const accountLoginUrl = root.dataset.accountLoginUrl || root.dataset.accountUrl || '/';
    const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    const defaultMenu = root.dataset.defaultMenu || $('.ref-mega-tab')?.dataset.menuTab || '';
    const openCartAfterAdd = root.dataset.openCartAfterAdd !== 'false';
    let searchTimer;

    /* V4.6 smart header: hide while scrolling down, reveal after a small upward scroll. */
    let lastScrollY = Math.max(0, window.scrollY || window.pageYOffset || 0);
    let scrollDirection = 0;
    let scrollTravel = 0;
    let scrollRaf = 0;

    const overlayIsOpen = () =>
      mega?.classList.contains('is-open') ||
      searchLayer?.classList.contains('is-open') ||
      cartDrawer?.classList.contains('is-open');

    const updateSmartHeader = () => {
      scrollRaf = 0;
      const y = Math.max(0, window.scrollY || window.pageYOffset || 0);
      const delta = y - lastScrollY;
      const barHeight = $('.ref-header__bar')?.offsetHeight || 84;

      if (Math.abs(delta) < 1) return;

      const direction = delta > 0 ? 1 : -1;
      if (direction !== scrollDirection) {
        scrollDirection = direction;
        scrollTravel = 0;
      }
      scrollTravel += Math.abs(delta);

      if (y <= 8) {
        root.classList.remove('ref-scroll-sticky', 'ref-scroll-hidden');
        scrollTravel = 0;
      } else {
        root.classList.add('ref-scroll-sticky');

        /* Do not change the underlying header while a drawer/menu/search is open. */
        if (!overlayIsOpen()) {
          if (direction > 0 && y > barHeight + 24 && scrollTravel >= 12) {
            root.classList.add('ref-scroll-hidden');
            scrollTravel = 0;
          } else if (direction < 0 && scrollTravel >= 6) {
            root.classList.remove('ref-scroll-hidden');
            scrollTravel = 0;
          }
        }
      }

      lastScrollY = y;
    };

    const onSmartScroll = () => {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(updateSmartHeader);
    };

    /* ----------------------------------------------------------------
       V6.3 — the dark veil is made storefront-proof.
       On the live shop other sections, apps and speed optimisers can paint
       over the header's stacking context or drop stylesheet rules, which left
       the veil invisible even though the drawer above it was drawn. So while
       an overlay is open the whole header is given the top stacking position,
       and the veil's own critical properties are written straight onto the
       element, where no theme or app stylesheet can cancel them. The overlays
       deliberately stay INSIDE the header, so every ".ref-header ..." rule —
       outline icons, box-sizing, fonts — keeps applying to them.
       ---------------------------------------------------------------- */
    const searchShade = $('.ref-search-layer__shade');
    const searchTop = $('.ref-search-layer__top');
    const VEIL_Z = 2147480000;

    let raisedNodes = [];
    const raiseHeader = (on) => {
      if (on) {
        if (raisedNodes.length) return;
        /* The section wrapper Shopify prints around the header carries its own
           z-index, so every stacking ancestor has to come up, not just the
           header itself. Each element's original inline style is kept. */
        let el = root;
        while (el && el.nodeType === 1 && el !== document.body) {
          const zIndex = getComputedStyle(el).zIndex;
          if (el === root || zIndex !== 'auto') {
            raisedNodes.push([el, el.getAttribute('style')]);
            if (getComputedStyle(el).position === 'static') {
              el.style.setProperty('position', 'relative', 'important');
            }
            el.style.setProperty('z-index', String(VEIL_Z), 'important');
          }
          el = el.parentElement;
        }
        return;
      }
      /* Only drop back once nothing is left open. */
      if (cartDrawer?.classList.contains('is-open')) return;
      if (searchLayer?.classList.contains('is-open')) return;
      raisedNodes.forEach(([el, previous]) => {
        if (previous === null) el.removeAttribute('style');
        else el.setAttribute('style', previous);
      });
      raisedNodes = [];
    };

    /* The header's icons are thin outlines, never filled shapes. Writing it on
       each svg keeps them correct even if a stylesheet rule goes missing. */
    const outlineIcons = (scope) => {
      if (!scope) return;
      scope.querySelectorAll('svg').forEach((svg) => {
        if (svg.closest('.ref-search-mic')) return; /* the microphone is solid */
        svg.style.setProperty('fill', 'none', 'important');
        svg.style.setProperty('stroke', 'currentColor', 'important');
      });
    };

    /* Measured from the reference: flat black at 46.5%, no blur, 0.2s fade. */
    const paintVeil = (el, on) => {
      if (!el) return;
      const s = el.style;
      s.setProperty('position', 'fixed', 'important');
      s.setProperty('top', '0', 'important');
      s.setProperty('right', '0', 'important');
      s.setProperty('bottom', '0', 'important');
      s.setProperty('left', '0', 'important');
      s.setProperty('display', 'block', 'important');
      s.setProperty('background-color', 'rgba(0,0,0,0.465)', 'important');
      s.setProperty('background-image', 'none', 'important');
      s.setProperty('backdrop-filter', 'none', 'important');
      s.setProperty('-webkit-backdrop-filter', 'none', 'important');
      s.setProperty('transition', 'opacity .2s ease', 'important');
      s.setProperty('opacity', on ? '1' : '0', 'important');
      s.setProperty('visibility', on ? 'visible' : 'hidden', 'important');
      s.setProperty('pointer-events', on ? 'auto' : 'none', 'important');
    };

    /* The reference search bar fades up over about 0.2s with a small scale. */
    const animateSearchTop = () => {
      if (!searchTop) return;
      const s = searchTop.style;
      s.setProperty('transition', 'none', 'important');
      s.setProperty('opacity', '0', 'important');
      s.setProperty('transform', 'scale(.985)', 'important');
      requestAnimationFrame(() => {
        s.setProperty('transition', 'opacity .2s ease,transform .22s cubic-bezier(.22,.61,.36,1)', 'important');
        s.setProperty('opacity', '1', 'important');
        s.setProperty('transform', 'scale(1)', 'important');
      });
    };

    const lock = (yes) => document.body.classList.toggle('ref-lock', !!yes);
    const escapeHtml = (str = '') => String(str).replace(/[&<>'"]/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#039;','"':'&quot;'}[c]));

    const formatMoney = (cents) => {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const value = Number(cents || 0) / 100;
      const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        if (isNaN(number) || number == null) return '0';
        const parts = Number(number).toFixed(precision).split('.');
        parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        return parts[0] + (parts[1] ? decimal + parts[1] : '');
      };
      return moneyFormat
        .replace(/{{\s*amount_no_decimals\s*}}/g, withDelimiters(value, 0))
        .replace(/{{\s*amount\s*}}/g, withDelimiters(value, 2))
        .replace(/{{\s*amount_with_comma_separator\s*}}/g, withDelimiters(value, 2, '.', ','))
        .replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/g, withDelimiters(value, 0, '.', ','));
    };

    const openSearchPanel = () => searchLayer?.classList.add('is-panel-open');
    const openSearch = () => {
      root.classList.remove('ref-scroll-hidden');
      closeMenu(false);
      closeCart(false);
      searchLayer?.classList.remove('is-panel-open');
      searchLayer?.classList.add('is-open');
      searchLayer?.setAttribute('aria-hidden', 'false');
      raiseHeader(true);
      paintVeil(searchShade, true);
      animateSearchTop();
      lock(true);
    };
    const closeSearch = (unlock = true) => {
      searchLayer?.classList.remove('is-open', 'is-panel-open');
      searchLayer?.setAttribute('aria-hidden', 'true');
      paintVeil(searchShade, false);
      raiseHeader(false);
      if (unlock && !cartDrawer?.classList.contains('is-open') && !mega?.classList.contains('is-open')) lock(false);
    };

    /* V5.3 department-context rule (MEN / WOMEN / JUNIORS etc.)
       -------------------------------------------------------------
       Desired behaviour:
       1) Fresh HOME landing -> Shopify menu #1 is bold.
       2) Click a top-level department -> that real Shopify page opens and that
          department becomes the active/bold department.
       3) Navigate deeper into that department (collection/category/product),
          refresh the browser, etc. -> the same department stays active.
       4) Click the logo from any child page -> return to that department's
          TOP-LEVEL landing page, not Home and not the current child page.
       5) Switching another top-level department replaces the saved context.

       We use sessionStorage only for the department context, while also
       resolving exact/descendant menu URLs from the real Shopify menu. This
       means refresh works and direct menu-linked child URLs can self-resolve. */
    let activeMenuKey = defaultMenu;
    const departmentStorageKey = `refDepartment:${root.dataset.sectionId || 'header'}`;

    const normalisePath = (url) => {
      try {
        const u = new URL(url || window.location.href, window.location.href);
        let path = u.pathname || '/';
        if (path.length > 1) path = path.replace(/\/+$/, '');
        return path.toLowerCase();
      } catch (_) {
        return '/';
      }
    };

    const topMenuLinks = () => $$('.ref-header__tab');

    const menuInfoForKey = (key) => {
      const link = topMenuLinks().find((a) => a.dataset.headerMenuKey === key);
      if (!link) return null;
      return {
        key,
        url: link.href,
        path: normalisePath(link.href)
      };
    };

    const readDepartment = () => {
      try {
        const raw = sessionStorage.getItem(departmentStorageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.key) return null;
        const current = menuInfoForKey(parsed.key);
        if (!current) return null;
        /* Always trust the CURRENT Shopify menu URL in case Admin changed it. */
        return current;
      } catch (_) {
        return null;
      }
    };

    const saveDepartment = (key) => {
      const info = menuInfoForKey(key);
      if (!info) return null;
      try {
        sessionStorage.setItem(departmentStorageKey, JSON.stringify({ key: info.key, url: info.url }));
      } catch (_) {}
      return info;
    };

    const clearDepartment = () => {
      try { sessionStorage.removeItem(departmentStorageKey); } catch (_) {}
    };

    const menuForCurrentPage = () => {
      const currentPath = normalisePath(window.location.href);
      const links = topMenuLinks();

      /* Exact top-level destination wins. */
      const exact = links.find((link) => normalisePath(link.href) === currentPath);
      if (exact?.dataset.headerMenuKey) return exact.dataset.headerMenuKey;

      /* Child path below a top-level landing path. */
      let best = null;
      let bestLength = -1;
      links.forEach((link) => {
        const path = normalisePath(link.href);
        if (path === '/' || path.length < 2) return;
        if (currentPath.startsWith(path + '/') && path.length > bestLength) {
          best = link;
          bestLength = path.length;
        }
      });
      if (best?.dataset.headerMenuKey) return best.dataset.headerMenuKey;

      /* Match URLs that live INSIDE each mega-menu panel. This lets a direct
         visit to a configured collection/category infer MEN/WOMEN/JUNIORS even
         when the top-level landing URL is not a pathname prefix. */
      let descendantMatch = null;
      let descendantLength = -1;
      $$('.ref-menu-panel').forEach((panel) => {
        const key = panel.dataset.panel;
        if (!key) return;
        $$('a[href]', panel).forEach((a) => {
          const path = normalisePath(a.href);
          if (path === '/' || path.length < 2) return;
          const isMatch = currentPath === path || currentPath.startsWith(path + '/');
          if (isMatch && path.length > descendantLength) {
            descendantMatch = key;
            descendantLength = path.length;
          }
        });
      });
      if (descendantMatch) return descendantMatch;

      return '';
    };

    const setHeaderActiveMenu = (tab) => {
      if (!tab) return;
      activeMenuKey = tab;
      $$('.ref-header__tab').forEach((link) => {
        link.classList.toggle('is-active', link.dataset.headerMenuKey === tab);
      });
      $$('.ref-mega-tab').forEach((link) => {
        link.classList.toggle('is-active', link.dataset.menuTab === tab);
      });
    };

    const switchMenu = (tab) => {
      setHeaderActiveMenu(tab);
      $$('.ref-menu-panel').forEach((p) => p.classList.toggle('is-active', p.dataset.panel === tab));
    };

    /* Resolve the current department. Home ALWAYS resets to menu #1. On every
       other page: URL-derived department wins; otherwise keep the department
       selected earlier in this same browsing tab. */
    const currentPathAtInit = normalisePath(window.location.href);
    if (currentPathAtInit === normalisePath(rootUrl)) {
      clearDepartment();
      activeMenuKey = defaultMenu;
      saveDepartment(activeMenuKey);
    } else {
      const resolvedFromUrl = menuForCurrentPage();
      const savedDepartment = readDepartment();
      activeMenuKey = resolvedFromUrl || savedDepartment?.key || defaultMenu;
      saveDepartment(activeMenuKey);
    }
    setHeaderActiveMenu(activeMenuKey);

    const departmentRootUrl = () => {
      return menuInfoForKey(activeMenuKey)?.url || readDepartment()?.url || rootUrl;
    };

    const syncLogoDestination = () => {
      const destination = departmentRootUrl();
      $$('.ref-header__logo, .ref-mega-logo').forEach((logo) => {
        logo.href = destination;
        logo.dataset.departmentHome = destination;
      });
    };
    syncLogoDestination();

    const openMenu = (tab = activeMenuKey || defaultMenu) => {
      root.classList.remove('ref-scroll-hidden');
      closeSearch(false);
      closeCart(false);
      if (tab) switchMenu(tab);
      mega?.classList.add('is-open');
      mega?.setAttribute('aria-hidden', 'false');
      $('.ref-menu-trigger')?.setAttribute('aria-expanded', 'true');
      lock(true);
    };

    function closeMenu(unlock = true) {
      mega?.classList.remove('is-open');
      mega?.setAttribute('aria-hidden', 'true');
      $('.ref-menu-trigger')?.setAttribute('aria-expanded', 'false');
      if (unlock && !cartDrawer?.classList.contains('is-open') && !searchLayer?.classList.contains('is-open')) lock(false);
    }

    const openCart = async () => {
      root.classList.remove('ref-scroll-hidden');
      closeSearch(false);
      closeMenu(false);
      /* The drawer replaces the small "item added" notification. */
      document.querySelector('.ref-cart-toast')?.classList.remove('is-visible');
      /* The reference drawer is narrow while the basket is empty and covers the
         whole mobile screen as soon as it holds a product. The known bag count
         sets that width before cart.js answers, so the panel never resizes. */
      const knownCount = Number((document.querySelector('.ref-cart-count')?.textContent || '').trim()) || 0;
      cartDrawer?.classList.toggle('ref-cart--empty', knownCount <= 0);
      raiseHeader(true);
      cartShade?.classList.add('is-open');
      paintVeil(cartShade, true);
      cartDrawer?.classList.add('is-open');
      cartDrawer?.setAttribute('aria-hidden', 'false');
      lock(true);
      await refreshCart();
    };

    function closeCart(unlock = true) {
      cartShade?.classList.remove('is-open');
      paintVeil(cartShade, false);
      cartDrawer?.classList.remove('is-open');
      cartDrawer?.setAttribute('aria-hidden', 'true');
      raiseHeader(false);
      if (unlock && !mega?.classList.contains('is-open') && !searchLayer?.classList.contains('is-open')) lock(false);
    }

    const updateCount = (count) => {
      $$('.ref-cart-count').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-empty', Number(count) === 0);
      });
    };

    const variantLabel = (item) => {
      const parts = [];
      (item.options_with_values || []).forEach((opt) => {
        const value = (opt?.value || '').trim();
        if (value && value !== 'Default Title') parts.push(value);
      });
      if (!parts.length && item.variant_title && item.variant_title !== 'Default Title') {
        parts.push(item.variant_title);
      }
      if (item.sku) parts.push(item.sku);
      return parts.join(' / ');
    };

    const trashIcon = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9.5 7V5.4A1.4 1.4 0 0 1 11 4h2a1.4 1.4 0 0 1 1.5 1.4V7M6.5 7l.8 12.2h9.4L17.5 7M10 10.5v6M14 10.5v6"/></svg>`;

    const renderCart = (cart) => {
      updateCount(cart.item_count || 0);
      const isEmpty = !cart.items || !cart.items.length;
      cartDrawer?.classList.toggle('ref-cart--empty', isEmpty);
      if (!cartDynamic) return;

      if (isEmpty) {
        cartDynamic.innerHTML = `
          <div class="ref-empty-cart">
            <h2>Your cart is empty</h2>
            <a class="ref-cart-primary" href="${rootUrl}">Continue shopping</a>
            <p>Have an account?</p>
            <p class="ref-empty-login"><a class="login-link" href="${accountLoginUrl}">Log in</a> to check out faster.</p>
          </div>`;
        return;
      }

      const lines = cart.items.map((item, index) => {
        const productUrl = item.url || '#';
        const variant = variantLabel(item);
        const img = item.image
          ? `<a class="ref-cart-line__image" href="${productUrl}"><img src="${item.image}" alt="${escapeHtml(item.product_title)}" loading="lazy"></a>`
          : `<a class="ref-cart-line__image" href="${productUrl}"></a>`;
        return `<div class="ref-cart-line" data-line="${index + 1}">
          ${img}
          <div class="ref-cart-line__meta">
            <a href="${productUrl}"><h4>${escapeHtml(item.product_title)}</h4></a>
            ${variant ? `<div class="variant">${escapeHtml(variant)}</div>` : ''}
            <div class="ref-cart-line__foot">
              <span class="ref-cart-line__qty">${item.quantity}x</span>
              <span class="price">${formatMoney(item.final_line_price)}</span>
            </div>
          </div>
          <button class="ref-remove" type="button" data-cart-change="${index + 1}" data-qty="0" aria-label="Remove ${escapeHtml(item.product_title)}">${trashIcon}</button>
        </div>`;
      }).join('');

      const note = (cartDrawer?.dataset.cartNote || '').trim();

      /* The product list scrolls inside its own fixed-height area, exactly like
         the reference drawer: total, buttons and recommendations stay in place. */
      cartDynamic.innerHTML = `<div class="ref-cart-lines">${lines}</div>
        <div class="ref-cart-summary">
          <div class="ref-cart-summary__row"><span>Total:</span><strong>${formatMoney(cart.total_price)}</strong></div>
          ${note ? `<p class="ref-cart-note">${escapeHtml(note)}</p>` : ''}
          <a class="ref-cart-checkout" href="${checkoutUrl}">Check out</a>
          <a class="ref-cart-view" href="${cartUrl}">View shopping basket</a>
        </div>`;
      outlineIcons(cartDynamic);
    };

    async function refreshCart() {
      if (!cartDynamic) return;
      try {
        const res = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('Cart request failed');
        renderCart(await res.json());
      } catch (e) {
        cartDynamic.innerHTML = '<div class="ref-cart-loading">Could not load cart. Please open the cart page.</div>';
      }
    }

    async function changeLine(line, quantity) {
      try {
        const res = await fetch(`${rootUrl}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
        });
        if (!res.ok) throw new Error('Cart update failed');
        renderCart(await res.json());
      } catch (e) {
        await refreshCart();
      }
    }

    const renderSearch = (products, query) => {
      if (!searchResults) return;
      if (!products?.length) {
        searchResults.innerHTML = `<div class="ref-search-empty">No products found for “${escapeHtml(query)}”.</div>`;
        return;
      }
      searchResults.innerHTML = products.slice(0, 6).map((p) => `
        <article class="ref-search-product">
          <a href="${p.url}">
            ${p.featured_image?.url ? `<img src="${p.featured_image.url}" alt="${escapeHtml(p.title)}" loading="lazy">` : ''}
            <h4>${escapeHtml(p.title)}</h4>
            <span class="price">${p.price}</span>${p.compare_at_price_max && p.compare_at_price_max !== p.price ? `<span class="compare">${p.compare_at_price_max}</span>` : ''}
          </a>
        </article>`).join('') + `<a class="ref-search-viewall" href="${rootUrl}search?q=${encodeURIComponent(query)}&type=product">View all results</a>`;
    };

    async function predictiveSearch(query) {
      const q = query.trim();
      if (!q) {
        if (searchStatus) {
          searchStatus.style.display = '';
          searchStatus.textContent = 'Start typing for search results';
        }
        if (searchResults) searchResults.innerHTML = '';
        return;
      }
      if (searchStatus) {
        searchStatus.style.display = '';
        searchStatus.textContent = 'Searching…';
      }
      try {
        const url = `${rootUrl}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('Search failed');
        const data = await res.json();
        const products = data?.resources?.results?.products || [];
        if (searchStatus) searchStatus.style.display = 'none';
        renderSearch(products, q);
      } catch (e) {
        if (searchStatus) searchStatus.textContent = 'Search is temporarily unavailable.';
        if (searchResults) searchResults.innerHTML = '';
      }
    }

    $$('.ref-search-trigger').forEach((b) => b.addEventListener('click', openSearch));
    $$('[data-close-search]').forEach((b) => b.addEventListener('click', () => closeSearch()));
    $('.ref-menu-trigger')?.addEventListener('click', () => openMenu());
    $$('[data-close-menu]').forEach((b) => b.addEventListener('click', () => closeMenu()));
    $$('.ref-header__logo, .ref-mega-logo').forEach((logo) => logo.addEventListener('click', (e) => {
      /* Logo = department HOME. Example: WOMEN -> Dresses collection -> logo
         returns to WOMEN landing page. MEN/JUNIORS behave the same. */
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
      const destination = departmentRootUrl();
      if (!destination) return;
      e.preventDefault();
      window.location.assign(destination);
    }));
    $$('.ref-header__tab').forEach((link) => link.addEventListener('click', () => {
      /* A real top-level department click replaces the browsing context before
         Shopify navigates. Refresh/deeper category navigation will keep it. */
      const key = link.dataset.headerMenuKey;
      setHeaderActiveMenu(key);
      saveDepartment(key);
      syncLogoDestination();
    }));
    $$('[data-menu-tab]').forEach((link) => link.addEventListener('click', (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
      e.preventDefault();
      const key = link.dataset.menuTab;
      switchMenu(key);
      saveDepartment(key);
      syncLogoDestination();
    }));
    $('[data-open-search-from-menu]')?.addEventListener('click', () => { closeMenu(false); requestAnimationFrame(() => openSearch()); });

    /* Force account navigation in CAPTURE phase. Some Shopify themes attach
       account-drawer handlers to the same icon and can cancel a normal <a>.
       Capture mode wins first, so this always opens the real Shopify account route. */
    root.addEventListener('click', (e) => {
      const accountTrigger = e.target.closest('.ref-account-trigger');
      if (!accountTrigger || !root.contains(accountTrigger)) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      window.location.assign(accountTrigger.getAttribute('href') || accountLoginUrl || accountUrl || '/');
    }, true);

    /* Same protection for “Log in to check out faster”. */
    cartDrawer?.addEventListener('click', (e) => {
      const login = e.target.closest('.login-link');
      if (!login) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      window.location.assign(login.getAttribute('href') || accountLoginUrl || accountUrl || '/');
    }, true);

    $$('[data-junior-tab]').forEach((btn) => btn.addEventListener('click', () => {
      const panel = btn.closest('.ref-menu-panel');
      if (!panel) return;
      $$('[data-junior-tab]', panel).forEach((b) => b.classList.toggle('is-active', b === btn));
      $$('[data-junior-panel]', panel).forEach((p) => p.classList.toggle('is-active', p.dataset.juniorPanel === btn.dataset.juniorTab));
    }));

    $$('.ref-cart-trigger').forEach((b) => b.addEventListener('click', openCart));
    $$('[data-close-cart]').forEach((b) => b.addEventListener('click', () => closeCart()));

    cartDrawer?.addEventListener('click', (e) => {
      const change = e.target.closest('[data-cart-change]');
      if (change) changeLine(change.dataset.cartChange, change.dataset.qty);
    });

    /* Voice search. The reference search pill carries a microphone beside the
       magnifier, so the same button is added here and wired to the browser's
       speech recognition. Browsers without it simply focus the field. */
    if (searchForm && !$('.ref-search-mic', searchForm)) {
      const mic = document.createElement('button');
      mic.type = 'button';
      mic.className = 'ref-search-mic';
      mic.setAttribute('aria-label', 'Search by voice');
      mic.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 14.5a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5.5a3 3 0 0 0 3 3Zm5.5-3a.9.9 0 0 0-1.8 0 3.7 3.7 0 0 1-7.4 0 .9.9 0 0 0-1.8 0 5.5 5.5 0 0 0 4.6 5.4V20a.9.9 0 0 0 1.8 0v-3.1a5.5 5.5 0 0 0 4.6-5.4Z"/></svg>`;
      searchForm.insertBefore(mic, $('.ref-search-submit', searchForm));
      mic.querySelector('svg')?.style.setProperty('fill', 'currentColor', 'important');
      mic.querySelector('svg')?.style.setProperty('stroke', 'none', 'important');

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      let recogniser = null;
      const micState = (listening) => {
        mic.classList.toggle('is-listening', listening);
        /* Written on the element so the colour shows even if the stylesheet
           rule is missing on the live storefront. */
        mic.style.setProperty('color', listening ? '#d11' : '#111', 'important');
      };
      const micSays = (text) => {
        if (!searchStatus) return;
        searchStatus.style.display = '';
        searchStatus.textContent = text;
      };

      mic.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        openSearchPanel();

        if (!SpeechRecognition) {
          micSays('Voice search is not supported in this browser. Please type instead.');
          searchInput?.focus();
          return;
        }
        if (recogniser) {
          try { recogniser.stop(); } catch (_) {}
          return;
        }

        recogniser = new SpeechRecognition();
        recogniser.lang = document.documentElement.lang || 'en-US';
        recogniser.interimResults = false;
        recogniser.maxAlternatives = 1;
        micState(true);
        micSays('Listening…');

        recogniser.onresult = (event) => {
          const said = event.results?.[0]?.[0]?.transcript || '';
          if (!said || !searchInput) return;
          searchInput.value = said;
          predictiveSearch(said);
        };
        recogniser.onend = () => {
          micState(false);
          recogniser = null;
          if (searchStatus && searchStatus.textContent === 'Listening…') {
            micSays('Start typing for search results');
          }
        };
        recogniser.onerror = (event) => {
          micState(false);
          recogniser = null;
          const denied = event?.error === 'not-allowed' || event?.error === 'service-not-allowed';
          micSays(denied
            ? 'Microphone access is blocked. Allow it in the browser address bar, or type instead.'
            : 'Could not hear anything. Please try again or type instead.');
          if (denied) searchInput?.focus();
        };

        try {
          recogniser.start();
        } catch (_) {
          micState(false);
          recogniser = null;
          searchInput?.focus();
        }
      });
    }

    /* First click on the icon = only big search bar. Second click/focus inside the bar = suggestions. */
    searchForm?.addEventListener('click', () => openSearchPanel());
    searchInput?.addEventListener('focus', () => openSearchPanel());
    searchInput?.addEventListener('input', (e) => {
      openSearchPanel();
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => predictiveSearch(e.target.value), 170);
    });

    /* Optional: make normal product forms open this same real-time AJAX drawer after Add to cart. */
    if (openCartAfterAdd && !document.documentElement.dataset.refCartInterceptor) {
      document.documentElement.dataset.refCartInterceptor = '1';
      document.addEventListener('submit', async (e) => {
        const form = e.target.closest('form[action*="/cart/add"]');
        if (!form || form.closest('.ref-header') || form.dataset.refSkipAjax === 'true') return;
        const submitter = e.submitter;
        e.preventDefault();
        if (submitter) submitter.disabled = true;
        try {
          const fd = new FormData(form);
          const res = await fetch(`${rootUrl}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
          if (!res.ok) throw new Error('Add to cart failed');
          await openCart();
        } catch (err) {
          form.dataset.refSkipAjax = 'true';
          form.submit();
        } finally {
          if (submitter) submitter.disabled = false;
        }
      }, true);
    }

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (cartDrawer?.classList.contains('is-open')) closeCart();
      else if (searchLayer?.classList.contains('is-open')) closeSearch();
      else if (mega?.classList.contains('is-open')) closeMenu();
    });

    window.addEventListener('scroll', onSmartScroll, { passive: true });
    updateSmartHeader();

    paintVeil(cartShade, false);
    paintVeil(searchShade, false);
    outlineIcons(searchLayer);
    outlineIcons(cartDrawer);
    /* Reference magnifier: a thin 1.5px outline, not a heavy one. */
    $('.ref-search-submit svg')?.style.setProperty('stroke-width', '1.5', 'important');

    refreshCart();
  };

  document.querySelectorAll('.ref-header').forEach(init);
  document.addEventListener('shopify:section:load', (e) => init(e.target.querySelector('.ref-header')));
})();

/* ================================================================
   Reference Mega Header V5.7 PATCH
   Stable blurred real-page preview + latest department navigation.

   Fixes:
   1) MEN -> WOMEN -> JUNIORS preview remains blurred at ALL times while
      the mega menu is open. There is no unblurred flash during iframe load.
   2) Switching back to MEN (or any earlier tab) always updates the latest
      pending destination, so X/Escape opens the MOST RECENTLY selected page.
   3) Top-level menu URLs are no longer rejected just because Shopify Theme
      Editor/custom-domain preview uses a different origin. Normal navigation
      always uses the exact menu link supplied by Shopify Content > Menus.
   4) Two iframe buffers keep the previous blurred preview visible while the
      newly selected real Shopify page loads, preventing white/unblurred flashes.
   ================================================================ */
(() => {
  const initV57 = (root) => {
    if (!root || root.dataset.refV57Init === '1') return;
    root.dataset.refV57Init = '1';

    const mega = root.querySelector('.ref-mega-layer');
    if (!mega) return;

    const sectionId = root.dataset.sectionId || Math.random().toString(36).slice(2);
    const storageKey = `refDepartment:${sectionId}`;
    let pendingUrl = '';
    let pendingKey = '';
    let previewToken = 0;
    let activeFrameIndex = -1;

    const isDesktop = () => window.matchMedia('(min-width:768px)').matches;

    /* Navigation must follow the exact Shopify menu link. Do not reject a valid
       custom-domain link merely because Theme Editor is currently on myshopify
       (or vice versa). Only non-http(s) schemes are rejected. */
    const navigationUrl = (value) => {
      if (!value) return '';
      try {
        const u = new URL(value, window.location.href);
        if (!/^https?:$/i.test(u.protocol)) return '';
        u.hash = '';
        return u.href;
      } catch (_) {
        return '';
      }
    };

    /* Keep preview-theme query values whenever present so the nested page uses
       the same Shopify draft theme during Theme Editor testing. */
    const previewUrl = (value) => {
      const href = navigationUrl(value);
      if (!href) return '';
      try {
        const dest = new URL(href);
        const current = new URL(window.location.href);
        ['preview_theme_id', 'preview_key', 'pb', '_fd'].forEach((name) => {
          if (!dest.searchParams.has(name) && current.searchParams.has(name)) {
            dest.searchParams.set(name, current.searchParams.get(name));
          }
        });
        return dest.href;
      } catch (_) {
        return href;
      }
    };

    const ensureStage = () => {
      let stage = document.getElementById(`ref-v57-stage-${sectionId}`);
      if (stage) return stage;

      stage = document.createElement('div');
      stage.id = `ref-v57-stage-${sectionId}`;
      stage.className = 'ref-v57-live-stage';
      stage.setAttribute('aria-hidden', 'true');

      for (let i = 0; i < 2; i += 1) {
        const frame = document.createElement('iframe');
        frame.className = 'ref-v57-live-frame';
        frame.dataset.refIndex = String(i);
        frame.setAttribute('title', 'Live department page preview');
        frame.setAttribute('aria-hidden', 'true');
        frame.setAttribute('tabindex', '-1');
        frame.loading = 'eager';
        stage.appendChild(frame);
      }

      const veil = document.createElement('div');
      veil.className = 'ref-v57-live-veil';
      stage.appendChild(veil);
      document.body.appendChild(stage);
      return stage;
    };

    const stage = ensureStage();
    const frames = [...stage.querySelectorAll('.ref-v57-live-frame')];

    const saveDepartment = (key, url) => {
      if (!key) return;
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({ key, url: url || '' }));
      } catch (_) {}
    };

    const setActive = (key) => {
      if (!key) return;
      root.querySelectorAll('.ref-header__tab').forEach((link) => {
        link.classList.toggle('is-active', link.dataset.headerMenuKey === key);
      });
      root.querySelectorAll('.ref-mega-tab').forEach((link) => {
        link.classList.toggle('is-active', link.dataset.menuTab === key);
      });
      root.querySelectorAll('.ref-menu-panel').forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.panel === key);
      });
    };

    const setLogoTarget = (url) => {
      if (!url) return;
      root.querySelectorAll('.ref-header__logo, .ref-mega-logo').forEach((logo) => {
        logo.href = url;
        logo.dataset.departmentHome = url;
      });
    };

    const markFrameActive = (index) => {
      if (index < 0 || !frames[index]) return;
      frames.forEach((frame, i) => {
        const yes = i === index;
        frame.classList.toggle('is-active', yes);
        frame.setAttribute('aria-hidden', yes ? 'false' : 'true');
      });
      activeFrameIndex = index;
      stage.classList.add('has-preview');
    };

    const hideStage = () => {
      previewToken += 1;
      stage.classList.remove('is-previewing', 'is-committing', 'has-preview');
      stage.setAttribute('aria-hidden', 'true');
      frames.forEach((frame) => frame.classList.remove('is-active', 'is-loading'));
      activeFrameIndex = -1;
    };

    const showPreview = (destination) => {
      if (!isDesktop()) return;
      const src = previewUrl(destination);
      if (!src) return;

      stage.classList.remove('is-committing');
      stage.classList.add('is-previewing');
      stage.setAttribute('aria-hidden', 'false');

      /* If one of the two buffers already contains this destination, switch to
         it immediately. This makes WOMEN -> MEN -> WOMEN feel instant. */
      const cachedIndex = frames.findIndex((frame) => frame.dataset.refUrl === src && frame.dataset.refLoaded === '1');
      if (cachedIndex >= 0) {
        previewToken += 1;
        markFrameActive(cachedIndex);
        return;
      }

      const token = ++previewToken;
      let incomingIndex = activeFrameIndex === 0 ? 1 : 0;
      if (activeFrameIndex < 0) incomingIndex = 0;
      const incoming = frames[incomingIndex];

      incoming.classList.remove('is-active');
      incoming.classList.add('is-loading');
      incoming.dataset.refLoaded = '0';
      incoming.dataset.refUrl = src;

      incoming.onload = () => {
        if (token !== previewToken || incoming.dataset.refUrl !== src) return;
        incoming.classList.remove('is-loading');
        incoming.dataset.refLoaded = '1';
        markFrameActive(incomingIndex);
      };
      incoming.onerror = () => {
        if (token !== previewToken) return;
        incoming.classList.remove('is-loading');
        incoming.dataset.refLoaded = '0';
        /* Keep the previous blurred frame visible. Navigation will still use
           pendingUrl when the customer closes the mega menu. */
      };

      /* Setting src on the hidden buffer does not remove the currently active
         blurred preview, so there is no unblurred/white flash while switching. */
      if (incoming.src !== src) incoming.src = src;
    };

    const resetPending = () => {
      pendingUrl = '';
      pendingKey = '';
      root.dataset.refV57PendingUrl = '';
      root.dataset.refV57PendingKey = '';
      hideStage();
    };

    /* Capture phase overrides the older V5.3/V5.5 mega-tab bubble handler. */
    root.addEventListener('click', (e) => {
      const megaTab = e.target.closest('[data-menu-tab]');
      if (megaTab && root.contains(megaTab) && mega.classList.contains('is-open')) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

        const key = megaTab.dataset.menuTab || '';
        const destination = navigationUrl(megaTab.dataset.menuUrl || megaTab.getAttribute('href') || megaTab.href);
        if (!key || !destination) return;

        /* IMPORTANT: update pending destination BEFORE doing any preview work. */
        pendingKey = key;
        pendingUrl = destination;
        root.dataset.refV57PendingKey = key;
        root.dataset.refV57PendingUrl = destination;

        setActive(key);
        saveDepartment(key, destination);
        setLogoTarget(destination);
        showPreview(destination);
        return;
      }

      const cancelForOtherOverlay = e.target.closest('[data-open-search-from-menu], .ref-cart-trigger');
      if (cancelForOtherOverlay && mega.classList.contains('is-open')) {
        resetPending();
      }

      const trigger = e.target.closest('.ref-menu-trigger');
      if (trigger && root.contains(trigger)) {
        resetPending();
      }
    }, true);

    document.addEventListener('shopify:section:unload', (e) => {
      if (!e.target?.contains(root)) return;
      stage.remove();
    });
  };

  document.querySelectorAll('.ref-header').forEach(initV57);
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target?.matches?.('.ref-header') ? e.target : e.target?.querySelector?.('.ref-header');
    initV57(root);
  });
})();

/* ================================================================
   Reference Mega Header V5.8 PATCH
   Near-instant, visually seamless department navigation after mega close.

   Goals:
   - Keep the already-loaded real page preview as a visual bridge.
   - Remove the old 500ms post-close wait.
   - Let the browser perform a REAL Shopify navigation so all products,
     sections, apps and theme scripts initialize normally.
   ================================================================ */
(() => {
  const initV58 = (root) => {
    if (!root || root.dataset.refV58Init === '1') return;
    root.dataset.refV58Init = '1';

    const mega = root.querySelector('.ref-mega-layer');
    if (!mega) return;

    const sectionId = root.dataset.sectionId || '';
    const stage = sectionId ? document.getElementById(`ref-v57-stage-${sectionId}`) : null;
    let navTimer = 0;
    let navigating = false;

    const pendingDestination = () => {
      const value = root.dataset.refV57PendingUrl || '';
      try {
        const u = new URL(value, window.location.href);
        if (!/^https?:$/i.test(u.protocol)) return '';
        return u.href;
      } catch (_) {
        return '';
      }
    };

    const samePage = (url) => {
      try {
        const a = new URL(url, window.location.href);
        const b = new URL(window.location.href);
        const clean = (u) => (u.pathname.replace(/\/+$/, '') || '/').toLowerCase();
        return a.origin === b.origin && clean(a) === clean(b);
      } catch (_) {
        return false;
      }
    };

    const commitFastNavigation = (event) => {
      const destination = pendingDestination();
      if (!destination || navigating) return false;
      /* Nothing chosen in this open session, or the chosen department is the
         page already on screen: close only, never reload. */
      if (root.dataset.refV510MenuChanged !== '1' || samePage(destination)) return false;
      navigating = true;

      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
      }

      clearTimeout(navTimer);

      /* Start the close immediately. The loaded iframe preview expands to the
         viewport and sharpens, so the user is already looking at the target
         page before the real Shopify navigation replaces the document. */
      mega.classList.remove('is-open');
      mega.setAttribute('aria-hidden', 'true');
      root.querySelector('.ref-menu-trigger')?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('ref-lock');
      document.documentElement.classList.add('ref-v58-navigating');

      if (stage && window.matchMedia('(min-width:768px)').matches) {
        stage.classList.add('is-previewing', 'is-committing', 'ref-v58-commit');
        stage.setAttribute('aria-hidden', 'false');
      }

      /* Old V5.7 waited ~500ms AFTER close. V5.8 navigates at the first clean
         visual hand-off, while the outgoing full-page preview masks the reload. */
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const delay = reduceMotion ? 24 : (window.matchMedia('(min-width:768px)').matches ? 115 : 70);

      navTimer = window.setTimeout(() => {
        /* assign() intentionally performs a real Shopify navigation. Products,
           lazy sections, apps and theme JS therefore initialize normally. */
        window.location.assign(destination);
      }, delay);
      return true;
    };

    /* Document capture runs before the older root-level V5.7 close handler,
       preventing its 500ms timer from being scheduled. */
    document.addEventListener('click', (event) => {
      if (!mega.classList.contains('is-open')) return;
      const close = event.target.closest?.('[data-close-menu]');
      if (!close || !root.contains(close)) return;
      if (pendingDestination()) commitFastNavigation(event);
    }, true);

    /* Window capture runs before V5.7's document-level Escape handler. */
    window.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !mega.classList.contains('is-open')) return;
      if (pendingDestination()) commitFastNavigation(event);
    }, true);

    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.target?.contains(root)) return;
      clearTimeout(navTimer);
    });
  };

  document.querySelectorAll('.ref-header').forEach(initV58);
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.matches?.('.ref-header')
      ? event.target
      : event.target?.querySelector?.('.ref-header');
    initV58(root);
  });
})();

/* ================================================================
   Reference Mega Header V5.9 PATCH
   - Department landing pages keep transparent-header behaviour.
   - Product/collection child pages hide closed MEN/WOMEN/JUNIORS tabs.
   - Cart count updates immediately after ANY AJAX add-to-cart.
   - A top-right "Item added to your cart" notification appears immediately,
     with the image of the product that was just added.
   ================================================================ */
(() => {
  if (window.__refV59GlobalInstalled) return;
  window.__refV59GlobalInstalled = true;

  const cartRoot = () => document.querySelector('.ref-header');
  const rootUrl = () => cartRoot()?.dataset.rootUrl || '/';
  let toastTimer = 0;
  let reconcileTimer = 0;
  let lastNotifyAt = 0;

  const badgeEls = () => [...document.querySelectorAll('.ref-cart-count')];
  const currentCount = () => {
    const el = badgeEls()[0];
    const n = Number.parseInt(el?.textContent || '0', 10);
    return Number.isFinite(n) ? n : 0;
  };
  const setCount = (count) => {
    const value = Math.max(0, Number(count) || 0);
    badgeEls().forEach((el) => {
      el.textContent = String(value);
      el.classList.toggle('is-empty', value <= 0);
    });
  };

  /* The circle in the notification shows the product that was just added. */
  const setToastImage = (src) => {
    const icon = ensureToast().querySelector('.ref-cart-toast__icon');
    if (!icon) return;
    if (!src) {
      icon.classList.remove('has-image');
      return;
    }
    let img = icon.querySelector('img');
    if (!img) {
      img = document.createElement('img');
      img.alt = '';
      img.loading = 'lazy';
      icon.appendChild(img);
    }
    img.src = src;
    icon.classList.add('has-image');
  };

  const cartLineImage = (cart, variantId) => {
    const items = cart?.items || [];
    if (!items.length) return '';
    const wanted = variantId ? String(variantId) : '';
    const match = wanted ? items.find((item) => String(item.variant_id) === wanted || String(item.id) === wanted) : null;
    return (match || items[items.length - 1])?.image || '';
  };

  const ensureToast = () => {
    let toast = document.querySelector('.ref-cart-toast');
    if (toast) return toast;
    toast = document.createElement('div');
    toast.className = 'ref-cart-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `
      <span class="ref-cart-toast__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24"><path d="M7 6.8h10l.8 11.4H6.2L7 6.8Zm2.2.2V5.8a2.8 2.8 0 0 1 5.6 0V7h-1.5V5.8a1.3 1.3 0 0 0-2.6 0V7H9.2Z"/></svg>
      </span>
      <span class="ref-cart-toast__check" aria-hidden="true">✓</span>
      <span class="ref-cart-toast__text">Item added to your cart</span>`;
    document.body.appendChild(toast);
    return toast;
  };

  const showToast = () => {
    const toast = ensureToast();
    clearTimeout(toastTimer);
    requestAnimationFrame(() => {
      /* If the cart drawer itself opened, it already shows what was added. */
      if (document.querySelector('.ref-cart-drawer.is-open')) {
        toast.classList.remove('is-visible');
        return;
      }
      toast.classList.add('is-visible');
    });
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2800);
  };

  const variantFromBody = (body) => {
    try {
      if (body instanceof FormData || body instanceof URLSearchParams) return body.get('id') || '';
      if (typeof body === 'string' && body) {
        try {
          const parsed = JSON.parse(body);
          if (Array.isArray(parsed?.items)) return parsed.items[0]?.id || '';
          return parsed?.id || '';
        } catch (_) {
          return new URLSearchParams(body).get('id') || '';
        }
      }
    } catch (_) {}
    return '';
  };

  const quantityFromBody = (body) => {
    try {
      if (body instanceof FormData || body instanceof URLSearchParams) {
        const q = Number(body.get('quantity') || 1);
        return Number.isFinite(q) && q > 0 ? q : 1;
      }
      if (typeof body === 'string' && body) {
        try {
          const parsed = JSON.parse(body);
          if (Array.isArray(parsed?.items)) {
            return parsed.items.reduce((sum, item) => sum + Math.max(1, Number(item?.quantity || 1)), 0);
          }
          const q = Number(parsed?.quantity || 1);
          if (Number.isFinite(q) && q > 0) return q;
        } catch (_) {
          const params = new URLSearchParams(body);
          const q = Number(params.get('quantity') || 1);
          if (Number.isFinite(q) && q > 0) return q;
        }
      }
    } catch (_) {}
    return 1;
  };

  const reconcileCart = (variantId = '') => {
    clearTimeout(reconcileTimer);
    reconcileTimer = window.setTimeout(async () => {
      try {
        const response = await window.fetch(`${rootUrl()}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        if (!response.ok) return;
        const cart = await response.json();
        setCount(cart.item_count || 0);
        if (variantId !== null) setToastImage(cartLineImage(cart, variantId));
      } catch (_) {}
    }, 60);
  };

  const notifySuccessfulAdd = (quantity = 1, variantId = '') => {
    /* A theme and this custom header can observe the same request through more
       than one hook. Deduplicate the UI notification while still reconciling. */
    const now = Date.now();
    if (now - lastNotifyAt > 320) {
      setCount(currentCount() + Math.max(1, Number(quantity) || 1));
      showToast();
      lastNotifyAt = now;
    }
    reconcileCart(variantId);
  };

  const isCartAddUrl = (value) => {
    try {
      const url = new URL(typeof value === 'string' ? value : value?.url || '', window.location.href);
      return /\/cart\/add(?:\.js)?\/?$/i.test(url.pathname);
    } catch (_) {
      return false;
    }
  };

  /* Observe Shopify/theme fetch-based add-to-cart requests without forcing the
     theme to use our drawer. This keeps the store's existing product logic. */
  if (!window.__refV59FetchWrapped && typeof window.fetch === 'function') {
    window.__refV59FetchWrapped = true;
    const nativeFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const input = args[0];
      const init = args[1] || {};
      const isAdd = isCartAddUrl(input);
      const qty = isAdd ? quantityFromBody(init.body) : 0;
      const variantId = isAdd ? variantFromBody(init.body) : '';
      const response = await nativeFetch(...args);
      if (isAdd && response.ok) notifySuccessfulAdd(qty || 1, variantId);
      return response;
    };
  }

  /* A few Shopify themes still use XMLHttpRequest for cart/add.js. */
  if (!window.__refV59XhrWrapped && window.XMLHttpRequest) {
    window.__refV59XhrWrapped = true;
    const nativeOpen = XMLHttpRequest.prototype.open;
    const nativeSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this.__refV59IsCartAdd = isCartAddUrl(url);
      return nativeOpen.call(this, method, url, ...rest);
    };
    XMLHttpRequest.prototype.send = function(body) {
      const qty = this.__refV59IsCartAdd ? quantityFromBody(body) : 0;
      const variantId = this.__refV59IsCartAdd ? variantFromBody(body) : '';
      if (this.__refV59IsCartAdd) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) notifySuccessfulAdd(qty || 1, variantId);
        }, { once: true });
      }
      return nativeSend.call(this, body);
    };
  }

  /* Also react to common custom events fired by Shopify themes/apps. */
  ['cart:updated', 'cart:refresh', 'product:added', 'cart:item-added'].forEach((name) => {
    document.addEventListener(name, () => reconcileCart(null), true);
  });

  /* On load, reconcile once so the badge is correct without requiring a cart click. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => reconcileCart(null), { once: true });
  } else {
    reconcileCart(null);
  }
})();

/* ================================================================
   Reference Mega Header V5.11 FINAL PATCH
   - Opening + closing the hamburger without choosing any menu NEVER reloads.
   - Closed-header MEN/WOMEN/JUNIORS open their page directly with a normal
     Shopify page load (also inside the Theme Editor), so products always load.
   - Closing the menu reloads ONLY if a different department was chosen.
   - Keeps department context for logo/back-to-department behaviour.
   - Adds a small smooth cart-icon bump when the real-time cart count changes.
   ================================================================ */
(() => {
  if (window.__refV510Installed) return;
  window.__refV510Installed = true;

  const clearPendingNavigation = (root) => {
    root.dataset.refV510MenuChanged = '0';
    root.dataset.refV57PendingUrl = '';
    root.dataset.refV57PendingKey = '';
    document.documentElement.classList.remove('ref-v58-navigating');

    const sectionId = root.dataset.sectionId || '';
    const stage = sectionId ? document.getElementById(`ref-v57-stage-${sectionId}`) : null;
    if (stage) {
      stage.classList.remove('is-previewing', 'is-committing', 'has-preview', 'ref-v58-commit');
      stage.setAttribute('aria-hidden', 'true');
      stage.querySelectorAll('.ref-v57-live-frame').forEach((frame) => {
        frame.classList.remove('is-active', 'is-loading');
      });
    }
  };

  const closeMegaOnly = (root, event) => {
    const mega = root.querySelector('.ref-mega-layer');
    if (!mega?.classList.contains('is-open')) return false;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    }

    /* This is intentionally a VISUAL close only. No location.assign(),
       pushState(), reload(), or history mutation is allowed here. */
    mega.classList.remove('is-open');
    mega.setAttribute('aria-hidden', 'true');
    root.querySelector('.ref-menu-trigger')?.setAttribute('aria-expanded', 'false');
    root.classList.remove('ref-page-switching');
    document.body.classList.remove('ref-lock');
    clearPendingNavigation(root);
    return true;
  };

  const roots = () => [...document.querySelectorAll('.ref-header')];

  const cleanPath = (u) => (u.pathname.replace(/\/+$/, '') || '/').toLowerCase();

  /* True when the department chosen inside the open menu is the page that is
     already on screen, so closing must not reload it. */
  const pendingIsCurrentPage = (root) => {
    const value = root.dataset.refV57PendingUrl || '';
    if (!value) return true;
    try {
      const target = new URL(value, window.location.href);
      return target.origin === window.location.origin && cleanPath(target) === cleanPath(new URL(window.location.href));
    } catch (_) {
      return true;
    }
  };

  /* WINDOW CAPTURE: MEN / WOMEN / JUNIORS in the CLOSED header open their page
     directly — the same way they open from inside the hamburger menu. A plain
     link is not enough inside Shopify's Theme Editor (the editor swallows the
     click to select the section), so the navigation is done here explicitly. */
  window.addEventListener('click', (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    const link = event.target?.closest?.('.ref-header__tab');
    if (!link) return;
    const root = link.closest('.ref-header');
    if (!root) return;
    if (root.querySelector('.ref-mega-layer')?.classList.contains('is-open')) return;

    let destination = '';
    try {
      const u = new URL(link.getAttribute('href') || link.href, window.location.href);
      if (/^https?:$/i.test(u.protocol)) destination = u.href;
    } catch (_) {}
    if (!destination) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    const key = link.dataset.headerMenuKey || '';
    if (key) {
      try {
        sessionStorage.setItem(`refDepartment:${root.dataset.sectionId || 'header'}`, JSON.stringify({ key, url: destination }));
      } catch (_) {}
    }
    root.querySelectorAll('.ref-header__tab').forEach((item) => item.classList.toggle('is-active', item === link));

    window.location.assign(destination);
  }, true);

  /* DOCUMENT CAPTURE: reset the current hamburger-open session BEFORE older
     V5.7/V5.8 handlers see the event. */
  document.addEventListener('click', (event) => {
    const trigger = event.target?.closest?.('.ref-menu-trigger');
    if (trigger) {
      const root = trigger.closest('.ref-header');
      if (root) clearPendingNavigation(root);
      return;
    }

    const megaTab = event.target?.closest?.('[data-menu-tab]');
    if (megaTab) {
      const root = megaTab.closest('.ref-header');
      const mega = root?.querySelector('.ref-mega-layer');
      if (root && mega?.classList.contains('is-open')) {
        root.dataset.refV510MenuChanged = '1';
      }
      return;
    }

    const close = event.target?.closest?.('[data-close-menu]');
    if (!close) return;
    const root = close.closest('.ref-header');
    const mega = root?.querySelector('.ref-mega-layer');
    if (!root || !mega?.classList.contains('is-open')) return;

    /* KEY FIX: hamburger was only opened and then closed. Because no top-level
       menu was chosen during THIS open session, stop every older commit handler
       and simply close the curtain. The current page is untouched. */
    if (root.dataset.refV510MenuChanged !== '1' || pendingIsCurrentPage(root)) {
      closeMegaOnly(root, event);
    }
  }, true);

  /* Escape must obey the same "no selection = no reload" rule. */
  window.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    for (const root of roots()) {
      const mega = root.querySelector('.ref-mega-layer');
      if (!mega?.classList.contains('is-open')) continue;
      if (root.dataset.refV510MenuChanged !== '1' || pendingIsCurrentPage(root)) {
        closeMegaOnly(root, event);
        break;
      }
    }
  }, true);

  /* Smooth real-time cart feedback. V5.9 already updates the badge instantly;
     this observer only adds a short visual bump to the bag/count. */
  const cartObservers = new WeakMap();
  const bindCartAnimation = (root) => {
    if (!root || root.dataset.refV510CartAnim === '1') return;
    root.dataset.refV510CartAnim = '1';

    const trigger = root.querySelector('.ref-cart-trigger');
    const count = root.querySelector('.ref-cart-count');
    if (!trigger || !count) return;

    let last = count.textContent;
    const bump = () => {
      trigger.classList.remove('ref-cart-bump');
      void trigger.offsetWidth;
      trigger.classList.add('ref-cart-bump');
      window.setTimeout(() => trigger.classList.remove('ref-cart-bump'), 560);
    };

    const observer = new MutationObserver(() => {
      const next = count.textContent;
      if (next === last) return;
      last = next;
      bump();
    });
    observer.observe(count, { childList: true, characterData: true, subtree: true });
    cartObservers.set(root, observer);
  };

  roots().forEach(bindCartAnimation);
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.matches?.('.ref-header')
      ? event.target
      : event.target?.querySelector?.('.ref-header');
    bindCartAnimation(root);
  });
})();